import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, CircleDollarSign, Clock, CreditCard, Download, Eye, Mail, Plus, Printer, RefreshCw, Trash2, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { DatePickerFr } from '../ui/DatePickerFr';
import { BACKEND_URL, MODES_PAIEMENT, MODE_LABEL } from './constants';
import { getToken } from '../../services/tokenService';
import { EcheancesSupprimees } from '../EcheancesSupprimees';

export default function EcheancierPanel({ recu, onClose, onRefreshList }) {
  const [echeancier, setEcheancier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [nbEcheances, setNbEcheances] = useState(3);
  const [echeancesForm, setEcheancesForm] = useState([]);
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm] = useState({ montant_paye: 0, mode_paiement: 'virement', reference_paiement: '', observations: '' });
  const [paying, setPaying] = useState(false);
  const [tick, setTick] = useState(0);
  const headers = { Authorization: `Bearer ${getToken()}` };

  const loadEcheancier = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/${recu.id}/echeancier`, { headers });
      setEcheancier(res.data);
    } catch (err) {
      if (err.response?.status === 404) setEcheancier(null);
      else setEcheancier({ echeances: [] });
    }
    setLoading(false);
  }, [recu.id]);

  useEffect(() => { loadEcheancier(); }, [loadEcheancier]);

  const generateForm = () => {
    const montant = parseFloat(recu.montant_ttc) || 0;
    const perEch = Math.round((montant / nbEcheances) * 100) / 100;
    const arr = [];
    let remaining = montant;
    const today = new Date();
    for (let i = 0; i < nbEcheances; i++) {
      const amt = i < nbEcheances - 1 ? perEch : Math.round(remaining * 100) / 100;
      remaining -= perEch;
      const d = new Date(today); d.setMonth(d.getMonth() + i + 1);
      arr.push({ montant: amt, date_echeance: d.toISOString().split('T')[0] });
    }
    setEcheancesForm(arr);
  };

  useEffect(() => { generateForm(); }, [nbEcheances, recu.montant_ttc]);

  const handleCreateEcheancier = async () => {
    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/recus-ess/${recu.id}/echeancier`, { echeances: echeancesForm }, { headers: { ...headers, 'Content-Type': 'application/json' } });
      toast.success('Echeancier cree avec succes');
      loadEcheancier();
      onRefreshList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur creation echeancier'); }
    setCreating(false);
  };

  const handleDeleteEcheancier = async () => {
    const payees = (echeancier?.echeances || []).filter(e => e.statut === 'paye').length;
    if (!window.confirm(payees
      ? `Supprimer tout l'échéancier ?\n\nAttention : ${payees} échéance(s) payée(s) seront aussi retirées et le reçu sera régénéré.`
      : "Supprimer tout l'échéancier ?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/recus-ess/${recu.id}/echeancier${payees ? '?forcer=true' : ''}`, { headers });
      toast.success('Echeancier supprime');
      setEcheancier(null);
      setTick((n) => n + 1);
      onRefreshList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur suppression'); }
  };

  const handleDeleteEcheance = async (ech) => {
    if (!window.confirm(`Supprimer l'échéance n°${ech.numero_echeance} (${parseFloat(ech.montant || 0).toFixed(2)} EUR)${ech.statut === 'paye' ? ' — déjà payée, son paiement sera retiré' : ''} ?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/recus-ess/${recu.id}/echeancier/${ech.id}`, { headers });
      toast.success('Échéance supprimée — reçu régénéré');
      setTick((n) => n + 1);
      await loadEcheancier();
      onRefreshList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur suppression'); }
  };

  const handlePay = async () => {
    if (!payModal) return;
    setPaying(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/recus-ess/${recu.id}/echeancier/${payModal.id}/payer`,
        payForm,
        { headers: { ...headers, 'Content-Type': 'application/json' } }
      );
      toast.success(res.data.message);
      setPayModal(null);
      loadEcheancier();
      onRefreshList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur paiement'); }
    setPaying(false);
  };

  const handleStripeCheckout = async (ech) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/recus-ess/${recu.id}/echeancier/${ech.id}/stripe-checkout`, {}, { headers });
      if (res.data.checkout_url) {
        window.open(res.data.checkout_url, '_blank');
        toast.success('Session Stripe ouverte dans un nouvel onglet');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur Stripe'); }
  };

  const handleViewRecuEcheance = async (ech) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/${recu.id}/echeancier/${ech.id}/recu-pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur generation recu"); }
  };

  const handleDownloadRecuEcheance = async (ech) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/${recu.id}/echeancier/${ech.id}/recu-pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `Recu_Echeance_${ech.numero_echeance}.pdf`; a.click();
    } catch { toast.error('Erreur telechargement'); }
  };

  const handlePrintRecuEcheance = async (ech) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/${recu.id}/echeancier/${ech.id}/recu-pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const iframe = document.createElement('iframe'); iframe.style.display = 'none'; iframe.src = url;
      document.body.appendChild(iframe); iframe.onload = () => { iframe.contentWindow.print(); };
    } catch { toast.error('Erreur impression'); }
  };

  const handleViewRecuAcquitte = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/${recu.id}/recu-acquitte`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDownloadRecuAcquitte = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/${recu.id}/recu-acquitte`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `Recu_ACQUITTE_${recu.numero_recu?.replace(/\//g, '-')}.pdf`; a.click();
    } catch { toast.error('Erreur telechargement'); }
  };

  const handlePrintRecuAcquitte = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/${recu.id}/recu-acquitte`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const iframe = document.createElement('iframe'); iframe.style.display = 'none'; iframe.src = url;
      document.body.appendChild(iframe); iframe.onload = () => { iframe.contentWindow.print(); };
    } catch { toast.error('Erreur impression'); }
  };

  if (loading) return <div className="flex justify-center py-10"><RefreshCw className="h-6 w-6 animate-spin text-orange-400" /></div>;

  const hasEcheances = echeancier && echeancier.echeances && echeancier.echeances.length > 0;
  const isAcquitte = echeancier?.statut_paiement === 'acquitte';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8" onClick={e => e.stopPropagation()} data-testid="echeancier-panel">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-t-2xl px-6 py-4 text-white flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><Clock className="h-5 w-5" /> Echeancier - {recu.numero_recu}</h3>
            <p className="text-orange-100 text-sm">{recu.raison_sociale} - {parseFloat(recu.montant_ttc || 0).toFixed(2)} EUR</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><XCircle className="h-6 w-6" /></button>
        </div>

        <div className="p-6 space-y-5">
          <EcheancesSupprimees listUrl={`${BACKEND_URL}/api/recus-ess/${recu.id}/echeancier-supprimees`} headers={headers} refreshKey={tick}
            restoreUrl={(t) => `${BACKEND_URL}/api/recus-ess/${recu.id}/echeancier-supprimees/${t.id}/restaurer`}
            onDone={async () => { await loadEcheancier(); onRefreshList(); }} testId="recu-echeances-supprimees" dateKey="date_echeance" />
          {/* ACQUITTE Banner */}
          {isAcquitte && (
            <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-4 text-center" data-testid="echeancier-acquitte-banner">
              <p className="text-2xl font-bold text-emerald-700 tracking-widest">ACQUITTEE</p>
              <p className="text-emerald-600 text-sm mt-1">Paiement integral recu - {parseFloat(echeancier.montant_paye || 0).toFixed(2)} EUR</p>
              <div className="flex gap-2 justify-center mt-3">
                <Button size="sm" onClick={handleViewRecuAcquitte} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-view-acquitte"><Eye className="h-4 w-4 mr-1" /> Visualiser</Button>
                <Button size="sm" variant="outline" onClick={handleDownloadRecuAcquitte} className="border-emerald-400 text-emerald-700" data-testid="btn-download-acquitte"><Download className="h-4 w-4 mr-1" /> Telecharger</Button>
                <Button size="sm" variant="outline" onClick={handlePrintRecuAcquitte} data-testid="btn-print-acquitte"><Printer className="h-4 w-4 mr-1" /> Imprimer</Button>
              </div>
            </div>
          )}

          {/* Progress bar */}
          {hasEcheances && (
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Paye: <strong className="text-emerald-600">{parseFloat(echeancier.montant_paye || 0).toFixed(2)} EUR</strong></span>
                <span className="text-gray-600">Reste: <strong className="text-red-500">{parseFloat(echeancier.reste_a_payer || 0).toFixed(2)} EUR</strong></span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-gradient-to-r from-emerald-500 to-emerald-400 h-3 rounded-full transition-all" style={{ width: `${Math.min(100, ((echeancier.montant_paye || 0) / (echeancier.montant_ttc || 1)) * 100)}%` }} />
              </div>
            </div>
          )}

          {/* Echeances Table */}
          {hasEcheances ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">N</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Date</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-600">Montant</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-600">Statut</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Mode</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {echeancier.echeances.map(ech => (
                    <tr key={ech.id} className="hover:bg-gray-50/50" data-testid={`echeance-row-${ech.id}`}>
                      <td className="px-3 py-2.5 font-medium">{ech.numero_echeance}</td>
                      <td className="px-3 py-2.5">{ech.date_echeance}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-orange-700">{parseFloat(ech.montant || 0).toFixed(2)} EUR</td>
                      <td className="px-3 py-2.5 text-center">
                        {ech.statut === 'paye' ? (
                          <Badge className="bg-emerald-700 text-white text-xs font-semibold"><CheckCircle className="h-3 w-3 mr-1" />Paye</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300"><Clock className="h-3 w-3 mr-1" />En attente</Badge>
                        )}
                        {ech.notifications && Object.keys(ech.notifications).length > 0 && (
                          <div className="mt-1"><Mail className="h-3 w-3 text-blue-400 inline-block" title={`${Object.keys(ech.notifications).length} notification(s) envoyee(s)`} /></div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-500">{ech.statut === 'paye' ? MODE_LABEL(ech.mode_paiement) : '-'}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex gap-1 justify-center">
                          {ech.statut === 'paye' ? (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleViewRecuEcheance(ech)} title="Visualiser le recu"><Eye className="h-4 w-4 text-blue-600" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDownloadRecuEcheance(ech)} title="Telecharger"><Download className="h-4 w-4 text-emerald-600" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handlePrintRecuEcheance(ech)} title="Imprimer"><Printer className="h-4 w-4 text-gray-600" /></Button>
                            </>
                          ) : (
                            <>
                              <Button size="sm" variant="outline" className="text-xs border-orange-300 text-orange-700" onClick={() => { setPayModal(ech); setPayForm({ montant_paye: ech.montant, mode_paiement: 'virement', reference_paiement: '', observations: '' }); }} data-testid={`btn-pay-${ech.id}`}>
                                <CircleDollarSign className="h-3.5 w-3.5 mr-1" /> Enregistrer
                              </Button>
                              <Button size="sm" variant="outline" className="text-xs border-indigo-300 text-indigo-700" onClick={() => handleStripeCheckout(ech)} data-testid={`btn-stripe-${ech.id}`}>
                                <CreditCard className="h-3.5 w-3.5 mr-1" /> Stripe
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteEcheance(ech)} title="Supprimer cette échéance" data-testid={`btn-delete-echeance-${ech.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* Create echeancier form */
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-3"><Clock className="h-4 w-4" /> Creer un echeancier de paiement</h4>
                <div className="flex items-center gap-3 mb-4">
                  <label className="text-sm text-gray-600">Nombre d'echeances :</label>
                  <select value={nbEcheances} onChange={e => setNbEcheances(parseInt(e.target.value))} className="px-3 py-1.5 border rounded-lg text-sm" data-testid="echeancier-nb-select">
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  {echeancesForm.map((ech, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white rounded-lg p-2.5 border">
                      <span className="text-xs font-semibold text-gray-500 w-8">{i + 1}.</span>
                      <input type="number" step="0.01" value={ech.montant} onChange={e => {
                        const arr = [...echeancesForm]; arr[i] = { ...arr[i], montant: parseFloat(e.target.value) || 0 }; setEcheancesForm(arr);
                      }} className="flex-1 px-2 py-1.5 border rounded text-sm" data-testid={`echeancier-montant-${i}`} />
                      <span className="text-xs text-gray-400">EUR</span>
                      <DatePickerFr value={ech.date_echeance} onChange={v => {
                        const arr = [...echeancesForm]; arr[i] = { ...arr[i], date_echeance: v }; setEcheancesForm(arr);
                      }} className="text-sm" id={`echeancier-date-${i}`} />
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-3 text-sm">
                  <span className="text-gray-500">Total: <strong className={Math.abs(echeancesForm.reduce((s, e) => s + (e.montant || 0), 0) - parseFloat(recu.montant_ttc || 0)) < 0.02 ? 'text-emerald-600' : 'text-red-600'}>
                    {echeancesForm.reduce((s, e) => s + (e.montant || 0), 0).toFixed(2)} EUR
                  </strong> / {parseFloat(recu.montant_ttc || 0).toFixed(2)} EUR</span>
                  <Button onClick={handleCreateEcheancier} disabled={creating} className="bg-orange-500 hover:bg-orange-600" data-testid="btn-create-echeancier">
                    {creating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />} Creer l'echeancier
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Delete echeancier */}
          {hasEcheances && (
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50" onClick={handleDeleteEcheancier} data-testid="btn-delete-echeancier">
                <Trash2 className="h-4 w-4 mr-1" /> Supprimer l'echeancier
              </Button>
            </div>
          )}
        </div>

        {/* Pay modal */}
        {payModal && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setPayModal(null)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="pay-echeance-modal">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CircleDollarSign className="h-5 w-5 text-orange-600" /> Enregistrer le paiement - Echeance N{payModal.numero_echeance}
              </h3>
              <div className="space-y-3">
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Montant (EUR) *</label>
                  <input type="number" step="0.01" value={payForm.montant_paye} onChange={e => setPayForm(p => ({ ...p, montant_paye: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="pay-montant" />
                </div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Mode de paiement</label>
                  <select value={payForm.mode_paiement} onChange={e => setPayForm(p => ({ ...p, mode_paiement: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="pay-mode">
                    {MODES_PAIEMENT.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Reference</label>
                  <input type="text" value={payForm.reference_paiement} onChange={e => setPayForm(p => ({ ...p, reference_paiement: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="pay-reference" />
                </div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Observations</label>
                  <textarea rows={2} value={payForm.observations} onChange={e => setPayForm(p => ({ ...p, observations: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="pay-observations" />
                </div>
              </div>
              <div className="flex gap-3 justify-end mt-5">
                <Button variant="outline" onClick={() => setPayModal(null)}>Annuler</Button>
                <Button onClick={handlePay} disabled={paying} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-confirm-pay">
                  {paying ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />} Confirmer le paiement
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

